/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from "react";
import { useLocalization } from "@/context/localization-context";
import { formatDateTime } from "@/lib/date-format";
import { cn } from "@/lib/utils";

/**
 * Generic views of evaluation data for the developer diagnostics: every field is shown (nested objects as
 * dotted paths, arrays of objects as tables), so that new backend fields appear without a frontend change.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isObjectArray = (value: unknown): value is Record<string, any>[] =>
  Array.isArray(value) && value.length > 0 && value.every(isPlainObject);

export function formatNumber(value: number) {
  // integers stay plain: pixel coordinates and ids read better without grouping
  if (Number.isInteger(value)) return String(value);
  const abs = Math.abs(value);
  if (abs !== 0 && (abs < 0.0001 || abs >= 1e7)) return value.toExponential(2);
  return String(Number(value.toPrecision(abs < 1 ? 4 : 6)));
}

/** One value as compact, typed text (null dimmed, booleans and numbers set apart). */
export function Value({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  if (typeof value === "boolean") {
    return <span className={value ? "text-foreground" : "text-muted-foreground"}>{String(value)}</span>;
  }
  if (typeof value === "number") {
    return <span className="tabular-nums">{formatNumber(value)}</span>;
  }
  if (typeof value === "string") {
    if (value === "") return <span className="text-muted-foreground/50">""</span>;
    if (ISO_DATE.test(value)) {
      const date = new Date(value);
      return (
        <span title={value} className="tabular-nums">
          {Number.isNaN(date.getTime()) ? value : formatDateTime(date)}
        </span>
      );
    }
    return (
      <span className="break-words" title={value.length > 80 ? value : undefined}>
        {value}
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground/50">[]</span>;
    if (value.every((item) => item === null || typeof item !== "object")) {
      return (
        <span className="font-mono text-xs">
          [
          {value.map((item, index) => (
            <span key={index}>
              {index > 0 && ", "}
              {typeof item === "number" ? formatNumber(item) : String(item)}
            </span>
          ))}
          ]
        </span>
      );
    }
  }
  return <span className="break-all font-mono text-xs">{JSON.stringify(value)}</span>;
}

type Entry =
  | { kind: "value"; path: string; value: unknown }
  | { kind: "table"; path: string; rows: Record<string, any>[] };

/** Leaves of an object as dotted paths; arrays of objects stay whole (shown as tables). */
export function flattenEntries(data: Record<string, any>, prefix = "", depth = 0): Entry[] {
  const entries: Entry[] = [];
  for (const [key, value] of Object.entries(data)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value) && Object.keys(value).length > 0 && depth < 6) {
      entries.push(...flattenEntries(value, path, depth + 1));
    } else if (isObjectArray(value)) {
      entries.push({ kind: "table", path, rows: value });
    } else {
      entries.push({ kind: "value", path, value });
    }
  }
  return entries;
}

/** Every field of an object: key / value rows, tables for lists of objects. */
export function KeyValueGrid({
  data,
  omit = [],
  className,
}: {
  data: Record<string, any> | null | undefined;
  /** top-level keys shown elsewhere */
  omit?: string[];
  className?: string;
}) {
  const { t } = useLocalization();
  if (!data) return <div className="text-xs text-muted-foreground">{t("common.noData")}</div>;
  const entries = flattenEntries(
    Object.fromEntries(Object.entries(data).filter(([key]) => !omit.includes(key))),
  );
  if (entries.length === 0) return <div className="text-xs text-muted-foreground">{t("common.noData")}</div>;
  return (
    <dl className={cn("grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm", className)}>
      {entries.map((entry) =>
        entry.kind === "value" ? (
          <KeyValueRow key={entry.path} label={entry.path}>
            <Value value={entry.value} />
          </KeyValueRow>
        ) : (
          <div key={entry.path} className="col-span-2 mt-1.5">
            <div className="mb-1 font-mono text-[11px] text-muted-foreground">
              {entry.path} <span className="opacity-60">({entry.rows.length})</span>
            </div>
            <ObjectTable rows={entry.rows} />
          </div>
        ),
      )}
    </dl>
  );
}

export function KeyValueRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="truncate py-0.5 font-mono text-[11px] leading-5 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 py-0.5 leading-5">{children}</dd>
    </>
  );
}

/** A list of objects as a compact table (columns = every key that occurs, nested objects flattened). */
export function ObjectTable({
  rows,
  columns,
  render,
  maxHeight = "18rem",
}: {
  rows: Record<string, any>[];
  columns?: string[];
  /** custom cell rendering per column */
  render?: Record<string, (row: Record<string, any>, index: number) => ReactNode>;
  maxHeight?: string;
}) {
  const flatRows = rows.map((row) =>
    Object.fromEntries(
      flattenEntries(row).map((entry) => [
        entry.path,
        entry.kind === "value" ? entry.value : entry.rows,
      ]),
    ),
  );
  const cols =
    columns ??
    flatRows.reduce<string[]>((acc, row) => {
      for (const key of Object.keys(row)) if (!acc.includes(key)) acc.push(key);
      return acc;
    }, []);

  return (
    <div className="overflow-auto rounded-md border" style={{ maxHeight }}>
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b">
            {cols.map((col) => (
              <th key={col} className="whitespace-nowrap px-2 py-1.5 text-left font-mono font-medium text-muted-foreground">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {flatRows.map((row, index) => (
            <tr key={index} className="border-b last:border-b-0 hover:bg-muted/30">
              {cols.map((col) => (
                <td key={col} className="whitespace-nowrap px-2 py-1 align-top">
                  {render?.[col] ? render[col](rows[index], index) : <Value value={row[col]} />}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
